import { c as _c } from "react/compiler-runtime";
import { useAppDispatch } from '@repo/store/maps/hooks';
import { setIsLocalPreview } from '@repo/store/maps/slices/mapData';
import type { MapData, MapInitializationData } from '@repo/types/maps/map-types';
import { useEffect, useMemo, useState } from 'react';
interface LocalMapPreviewRequest {
  url: string;
}
const LOCAL_PREVIEW_ENABLED_VALUE = 'true';
const LOCAL_MAP_DATA_URL_PARAM = 'localMapDataUrl';
export function useLocalMapPreviewData(providedMapData, canUseLocalPreview) {
  const $ = _c(16);
  const dispatch = useAppDispatch();
  const localMapDataUrl = canUseLocalPreview ? getLocalMapPreviewRequest()?.url ?? null : null;
  let t0;
  if ($[0] !== localMapDataUrl || $[1] !== providedMapData.mapConfig.externalId) {
    t0 = localMapDataUrl ? {
      url: localMapDataUrl,
      mapExternalId: providedMapData.mapConfig.externalId
    } : null;
    $[0] = localMapDataUrl;
    $[1] = providedMapData.mapConfig.externalId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const localPreviewRequest = t0;
  let t1;
  if ($[3] !== localPreviewRequest) {
    t1 = () => localPreviewRequest ? undefined : null;
    $[3] = localPreviewRequest;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const [previewMapData, setPreviewMapData] = useState(t1);
  let t2;
  let t3;
  if ($[5] !== dispatch || $[6] !== localPreviewRequest || $[7] !== previewMapData) {
    t2 = () => {
      dispatch(setIsLocalPreview(Boolean(localPreviewRequest) && previewMapData !== null));
    };
    t3 = [dispatch, localPreviewRequest, previewMapData];
    $[5] = dispatch;
    $[6] = localPreviewRequest;
    $[7] = previewMapData;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== localPreviewRequest) {
    t4 = () => {
      if (!localPreviewRequest) {
        setPreviewMapData(null);
        return;
      }
      const controller = new AbortController();
      setPreviewMapData(undefined);
      fetch(localPreviewRequest.url, {
        cache: "no-store",
        headers: {
          Accept: "application/json"
        },
        signal: controller.signal
      }).then(async response => {
        if (!response.ok) {
          throw new Error(`Local map data returned ${response.status}`);
        }
        setPreviewMapData(await response.json());
      }).catch(error => {
        if (!controller.signal.aborted) {
          console.warn("Failed to load local map preview data:", error);
          setPreviewMapData(null);
        }
      });
      return () => {
        controller.abort();
      };
    };
    t5 = [localPreviewRequest];
    $[10] = localPreviewRequest;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  if (previewMapData === undefined) {
    return;
  }
  if (!previewMapData) {
    return providedMapData;
  }
  let t6;
  if ($[13] !== previewMapData || $[14] !== providedMapData) {
    t6 = {
      ...providedMapData,
      mapData: previewMapData
    };
    $[13] = previewMapData;
    $[14] = providedMapData;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function getLocalMapPreviewRequest(): LocalMapPreviewRequest | null {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash);
  const localPreviewEnabled = searchParams.get('localPreview') === LOCAL_PREVIEW_ENABLED_VALUE;
  if (!localPreviewEnabled) return null;
  const url = hashParams.get(LOCAL_MAP_DATA_URL_PARAM);
  if (!url) return null;
  return {
    url
  };
}